import { useCallback, useState } from 'react';
import { Typography, Modal, TextField, Button, Banner, Spinner } from '@scale/scaleui';
import apiFetch from '@scale/frontend-lib/api-fetch';

export function LoadFromTaskModal({
  open,
  onClose,
  setConfig,
  setResponses,
}: {
  open: boolean;
  onClose: () => void;
  setConfig: (config: any) => void;
  setResponses: (responses: any) => void;
}) {
  const [error, setError] = useState<string>('');
  const [taskId, setTaskId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchConfigurationFromTask = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const task = await apiFetch<any>(null, `/corp-api/lookup/${taskId}`);
      if (!task) {
        setError('Unable to load task.');
        return;
      }
      const params = task.params;
      if (!params?.before && !params?.after) {
        setError('The loaded object does not appear to be a chat task');
        return;
      }
      await setResponses([]);
      await setConfig(task.params);
      const attempts = task.attempts;
      if (attempts) {
        const attempt = attempts[attempts.length - 1];
        const responses = attempt.response?.responses || [];
        setResponses(responses);
      }
      onClose();
    } catch (e) {
      setError('Unable to load task.');
    } finally {
      setIsLoading(false);
    }
  }, [setResponses, setConfig, setIsLoading, taskId, onClose]);

  return (
    <Modal open={open} onClose={onClose} title="Load Config From Task">
      <div className="flex flex-col gap-4">
        {error && <Banner variant="danger" title={error} background="neutral" />}
        <Typography.Paragraph2>
          Loading a configuration from a task will overwrite the current configuration and
          responses.
        </Typography.Paragraph2>
        <TextField
          id="load-from-task-modal-task-id"
          disabled={isLoading}
          value={taskId}
          onChange={e => setTaskId(e.target.value)}
          label="Task ID"
        />
        <div className="flex justify-end gap-2">
          {isLoading && <Spinner size="sm" />}
          <Button
            className="self-end"
            onClick={fetchConfigurationFromTask}
            disabled={isLoading || !taskId}
          >
            Load Configuration
          </Button>
        </div>
      </div>
    </Modal>
  );
}
